import React from 'react';
import { DataTableProps, DataTableColumn } from '../../types/dataTable';
import Pagination from './Pagination';

function DataTable<T = any>({
  columns,
  data,
  pagination,
  loading = false,
  error = null,
  emptyMessage = "데이터가 없습니다.",
  className = "",
  onRowClick,
  rowClassName = "",
}: DataTableProps<T>) {
  
  // 컬럼의 값을 가져오는 함수
  const getCellValue = (row: T, column: DataTableColumn<T>) => {
    const keys = column.key.split('.');
    let value = row as any;
    
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        value = undefined;
        break;
      }
    }
    
    return value;
  };

  // 정렬 클래스 반환
  const getAlignClass = (align?: string) => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  // 행 클래스명 반환
  const getRowClassName = (row: T, index: number) => {
    const baseClass = "hover:bg-slate-800/50 transition-colors";
    const customClass = typeof rowClassName === 'function' 
      ? rowClassName(row, index) 
      : rowClassName;
    
    return `${baseClass} ${customClass}`.trim();
  };

  return (
    <div className={`bg-slate-900 rounded-xl border border-slate-800 overflow-hidden ${className}`}>
      <div className="overflow-x-auto elegant-scrollbar">
        <table className="w-full">
          {/* 테이블 헤더 */}
          <thead>
            <tr className="border-b border-slate-800">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-4 text-xs font-medium text-slate-400 uppercase tracking-wider ${getAlignClass(column.align)}`}
                  style={{ width: column.width }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* 테이블 바디 */}
          <tbody className="divide-y divide-slate-800">
            {error ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center">
                  <div className="text-red-400 text-center">
                    <div className="text-lg font-medium mb-2">⚠️ 오류가 발생했습니다</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, index) => (
                <tr
                  key={index}
                  className={getRowClassName(row, index)}
                  onClick={() => onRowClick?.(row, index)}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => {
                    const cellValue = getCellValue(row, column);
                    
                    return (
                      <td
                        key={column.key}
                        className={`px-6 py-4 ${getAlignClass(column.align)}`}
                      >
                        {column.render ? column.render(cellValue, row, index) : (
                          <span className="text-slate-300">
                            {cellValue != null ? String(cellValue) : '-'}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center">
                  <p className="text-slate-400">{emptyMessage}</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          onPageChange={pagination.onPageChange}
        />
      )}
    </div>
  );
}

export default DataTable; 